import type { AppRole } from '@metycle/shared';
import {
  isBackendUnreachableError,
  useProfileQuery,
  type ProfileView,
} from '@/features/profile/api/profile';

export interface CurrentUser {
  user: ProfileView | null;
  roles: AppRole[];
  isLoading: boolean;
  isError: boolean;
  /** The profile request failed at the network level — the backend is down or still booting. */
  isBackendUnreachable: boolean;
  isAdmin: boolean;
  isInvestorOnly: boolean;
}

export function useCurrentUser(): CurrentUser {
  const query = useProfileQuery();
  const roles = (query.data?.roles ?? []) as AppRole[];
  return {
    user: query.data ?? null,
    roles,
    isLoading: query.isLoading,
    isError: query.isError,
    // Only the no-data boot state blocks the UI. A failing background refetch
    // on a loaded app also sets `failureReason`, but swapping the shell for
    // the retry screen would unmount live work (form drafts, open dialogs) on
    // a network blip — the retryer heals that case silently instead. A
    // network error never settles into `query.error` (the query retries
    // forever), so `failureReason` is the only signal needed here.
    isBackendUnreachable: query.isPending && isBackendUnreachableError(query.failureReason),
    isAdmin: roles.includes('admin'),
    isInvestorOnly: roles.length > 0 && roles.every((role) => role === 'investor'),
  };
}
